<template>
	<AppHeader
		:logo="logo"
		:headerButton1="headerButton1"
		:headerButton2="headerButton2"
	/>

	<UiElement class="d-flex flex-column h-100 py-0" :settings="appearance.body.settings" data-editor-key="general.appearance.body.settings">
		<div class="flex-grow-1">
			<UiElement
				class="product-image position-relative w-100"
				:settings="hasModifiers ? block2 : block1"
				data-editor-key="block1"
				style="overflow: hidden"
			>
				<img
					class="d-block w-100 h-100"
					:src="
						details?.image && details?.image !== 'null'
							? details.image
							: 'https://www.bifolcomatty.co.uk/wp-content/uploads/2019/08/placeholder-square.jpg'"
					alt
				/>

				<UiElement
					class="position-absolute bottom-0 start-0 d-flex align-items-end justify-content-between w-100"
					:settings="card1"
					data-editor-key="card1"
				>
					<UiText class="w-75 max-w-75 min-w-75" :settings="heading1" data-editor-key="heading1" :content="productName" />

					<UiText :settings="heading1" data-editor-key="heading1" :content="`${fullPrice}&nbsp;${currency}`" />
				</UiElement>
			</UiElement>

			<div style="height: 40px" />

			<UiText
				v-if="productDescriptions"
				:settings="hasModifiers ? heading3 : heading2"
				:data-editor-key="hasModifiers ? 'heading3' : 'heading2'"
				:content="productDescriptions"
			/>

			<UiText
				v-else-if="isEmbed"
				:settings="hasModifiers ? heading3 : heading2"
				:data-editor-key="hasModifiers ? 'heading3' : 'heading2'"
				content="Lorem ipsum dolor sit amet, consectetur adipisicing elit. Aliquid, culpa cumque dolorem ipsum iste laborum reiciendis repellat ut veniam veritatis?"
			/>

			<div style="height: 30px" />

			<router-link v-if="details?.allergens && details?.allergens.length > 0" :to="makePath('/allergens')" class="text-decoration-none">
				<UiText
					:settings="hasModifiers ? heading3 : heading2" :data-editor-key="hasModifiers ? 'heading3' : 'heading2'"
					:content="`${t('allergens')}: ${
						details?.allergens
							.map(item => item.shortName)
							.sort((a, b) => {
								const parse = str => {
									const match = str.match(/^(\d+)([a-z]*)$/i);
									return [parseInt(match[1]), match[2] || ''];
								};
								const [aNum, aSuf] = parse(a);
								const [bNum, bSuf] = parse(b);
								return aNum - bNum || aSuf.localeCompare(bSuf);
							})
							.join(', ')
					}`"
				/>
			</router-link>

			<UiText v-else-if="isEmbed" :settings="hasModifiers ? heading3 : heading2" :data-editor-key="hasModifiers ? 'heading3' : 'heading2'" content="Allergens: 1, 2, 3" />

			<div v-if="ingredients">
				<div
					v-for="(items, categoryId) in ingredients"
					:key="`ingredient-category-${categoryId}`"
					class="mb-4"
				>
					<UiText :settings="heading4" class="text-center" data-editor-key="heading4" :content="getCategoryName(categoryId)" />

					<div class="d-flex gap-4">
						<UiButton
							v-for="(item, index) in items"
							:key="`ingredient-category-item-${index}`"
							:active="isIngredientSelected(item)"
							:settings="buttonModifier"
							data-editor-key="buttonModifier"
							class="w-100 option"
							disableHover
							@touchstart="selectIngredient(item)"
							:content="getTranslatedName(item)"
						>
						</UiButton>
					</div>
				</div>
			</div>

			<div v-if="modifiers">
				<div
					v-for="(items, categoryId) in modifiers"
					:key="`modifier-category-${categoryId}`"
					class="mb-5"
				>
					<UiText :settings="heading4" class="text-center" data-editor-key="heading4" :content="getCategoryName(categoryId)" />

					<div class="d-flex flex-wrap gap-4">
						<UiButton
							v-for="(item, index) in items"
							:key="`modifier-category-item-${index}`"
							:active="isModifierSelected(item)"
							:settings="buttonModifier"
							data-editor-key="buttonModifier"
							class="w-100 option"
							disableHover
							@touchstart="selectModifier(item)"
							:content="getTranslatedName(item)"
						>
						</UiButton>
					</div>
				</div>
			</div>
		</div>

		<UiElement class="position-sticky bottom-0 start-0 w-100 px-0" :settings="appearance.body.settings" data-editor-key="general.appearance.body.settings">
			<div class="d-flex justify-content-center mb-5">
				<UiButtonAmount
					:settings="buttonAmount1"
					data-editor-key="buttonAmount1"
					:isAddDisabled="isAddDisabled"
					:product="selectedProduct"
				>
					<template #default="{ count }">
						<UiText :settings="textAmount1" data-editor-key="textAmount1" :content="count" />
					</template>
				</UiButtonAmount>
			</div>

			<div class="d-flex gap-4">
				<UiButton
					:settings="button1"
					:body="page.appearance.body.settings"
					class="w-50"
					@touchstart="backToProducts"
					:content="translations?.chooseAnother || '{chooseAnother}'"
				>
				</UiButton>

				<UiButton
					:settings="button2"
					:body="page.appearance.body.settings"
					class="w-50"
					right
					@touchstart="toSummary"
					:content="`${ translations?.totalsummary || '{totalsummary}' } ${ formatNumberWithSpaces(smartNumberFormat(total)) }&nbsp;${currency}`"
				>
				</UiButton>
			</div>
		</UiElement>
	</UiElement>

	<DialogEmptyOrder :show="isDialogEmptyOrder" @submit="isDialogEmptyOrder = false" />
</template>

<script setup>
	import { ref, computed } from 'vue';
	import { storeToRefs } from 'pinia';
	import { useRouter } from 'vue-router';

	import { useInheritedSettings } from '@/composables/useInheritedSettings';
	import { useKioskRoute } from '@/composables/useKioskRoute';

	import { useKioskStore } from '@/stores/useKioskStore';
	import { useThemeStore } from '@/stores/useThemeStore';
	import { useOrderStore } from '@/stores/useOrderStore';
	import { useCatalogStore } from '@/stores/useCatalogStore';
	import { useTranslationsStore } from '@/stores/useTranslationsStore';

	import AppHeader from '@/components/AppHeader.vue';
	import UiElement from '@/components/UI/UiElement.vue';
	import UiButtonAmount from '@/components/UI/UiButtonAmount.vue';
	import UiButton from '@/components/UI/UiButton.vue';
	import UiText from '@/components/UI/UiText.vue';
	import { useDataUtils } from '@/composables/useDataUtils';
	import { useTranslation } from '@/composables/useTranslation';
	import DialogEmptyOrder from '@/components/Dialogs/DialogEmptyOrder.vue';

	const router = useRouter();
	const { makePath } = useKioskRoute();

	const kioskStore = useKioskStore();
	const themeStore = useThemeStore();
	const orderStore = useOrderStore();
	const catalogStore = useCatalogStore();
	const translationsStore = useTranslationsStore();

	const isDialogEmptyOrder = ref(false);

	const { getCategoryName, getTranslatedName } = catalogStore;
	const { formatNumberWithSpaces, smartNumberFormat } = useDataUtils();
	const { t } = useTranslation();

	const { isEmbed, currency, locale } = storeToRefs(kioskStore);
	const { translations } = storeToRefs(translationsStore);
	const { product } = storeToRefs(catalogStore);
	const { theme } = storeToRefs(themeStore);
	const { total } = storeToRefs(orderStore);

	const { clearProduct } = catalogStore;

	const general = computed(() => theme.value.general);
	const page = computed(() => theme.value.pages.productDetails);
	const appearance = computed(() => page.value.appearance);
	const elements = computed(() => page.value.elements);

	const details = computed(() => {
		return product.value?.details || null;
	});

	const productName = computed(() => {
		return details.value?.names[locale.value]
			|| (locale.value === 'cs' && details.value?.names.cz)
			|| details.value?.names.en
			|| details.value?.name
			|| 'Product name';
	});

	const productDescriptions = computed(() => {
		return details.value?.descriptions[locale.value]
			|| (locale.value === 'cs' && details.value?.descriptions.cz)
			|| details.value?.descriptions.en
			|| details.value?.description
			|| '';
	});

	const productPrice = computed(() => {
		return Number(details.value?.price || 10).toFixed(2);
	});

	const selectedProduct = ref({
		label: productName.value,
		id: product?.value?.id,
		price: productPrice.value,
		details: details.value,
		modifiers: [],
		product_order_index: `${product?.value?.id}_`
	});
	const modifiersStorage = ref([]);
	const modifiersNote = ref({});

	const heading1 = useInheritedSettings(computed(() => elements.value?.heading1));
	const heading2 = useInheritedSettings(computed(() => elements.value?.heading2));
	const heading3 = useInheritedSettings(computed(() => elements.value?.heading3));
	const heading4 = useInheritedSettings(computed(() => elements.value?.heading4));
	const block1 = useInheritedSettings(computed(() => elements.value?.block1));
	const block2 = useInheritedSettings(computed(() => elements.value?.block2));
	const card1 = useInheritedSettings(computed(() => elements.value?.card1));
	const button1 = useInheritedSettings(computed(() => elements.value?.button1));
	const button2 = useInheritedSettings(computed(() => elements.value?.button2));
	const buttonModifier = useInheritedSettings(computed(() => general.value?.elements?.buttonModifiers));
	const logo = useInheritedSettings(computed(() => elements.value?.logo));
	const headerButton1 = useInheritedSettings(computed(() => elements.value?.headerButton1));
	const headerButton2 = useInheritedSettings(computed(() => elements.value?.headerButton2));
	const buttonAmount1 = useInheritedSettings(computed(() => elements.value?.buttonAmount1));
	const textAmount1 = useInheritedSettings(computed(() => elements.value?.textAmount1));

	const hasModifiers = computed(() => (ingredients.value || modifiers.value));

	const ingredients = computed(() => {
		if (!details.value?.ingredients || details.value?.ingredients.length === 0) return;

		const res = details.value?.ingredients.reduce((acc, item) => {
			const cid = item.cid;

			if (!acc[cid]) {
				acc[cid] = [];
			}

			acc[cid].push(item);

			return acc;
		}, {});

		console.log(res);

		return res;
	});

	const modifiers = computed(() => {
		if (!details.value?.modifier || !details.value?.modifier?.modifiers || details.value?.modifier?.modifiers?.length === 0) return;

		return details.value?.modifier?.modifiers?.reduce((acc, item) => {
			console.log(item);

			const cid = item.cid;

			if (!acc[cid]) {
				acc[cid] = [];
			}

			acc[cid].push(item);

			return acc;
		}, {});
	});

	const fullPrice = computed(() => {
		const basePrice = Number(details.value?.price || 0);
		const modifiersTotal = modifiersStorage.value.reduce((sum, mod) => {
			return sum + (Number(mod.price) || 0);
		}, 0);

		return smartNumberFormat(basePrice + modifiersTotal);
	});

	const requireModifiers = ref(false);

	const isAddDisabled = computed(() => {
		if (!ingredients.value && (!requireModifiers.value || !modifiers.value)) return false;

		let missingIngredient = false;
		let missingModifier = false;

		if (ingredients.value) {
			const ingredientCategories = Object.keys(ingredients.value);
			const selectedIngredientCategories = Object.keys(modifiersNote.value);
			missingIngredient = ingredientCategories.some(cid => !selectedIngredientCategories.includes(cid));
		}

		if (requireModifiers.value && modifiers.value) {
			const modifierCategories = Object.keys(modifiers.value).map(String);
			const selectedModifierCategories = new Set(modifiersStorage.value.map(m => String(m.cid)));
			missingModifier = modifierCategories.some(cid => !selectedModifierCategories.has(cid));
		}

		return missingIngredient || (requireModifiers.value && missingModifier);
	});

	function backToProducts() {
		setTimeout(() => {
			clearProduct();
		}, 100);

		router.go(-1);
	}

	function isModifierSelected(item) {
		return modifiersStorage.value.some(m => m.id === item.id);
	}

	function isIngredientSelected(item) {
		return modifiersNote.value[item.cid]?.id === item.id;
	}

	function selectModifier(modifier) {
		const exists = modifiersStorage.value.find(m => m.id === modifier.id);

		if (exists) {
			modifiersStorage.value = modifiersStorage.value.filter(m => m.id !== modifier.id);
		} else {
			modifiersStorage.value.push({ ...modifier, type: 'modifier' });
		}

		updateSelectedProduct();
	}

	function selectIngredient(ingredient) {
		const exists = modifiersNote.value[ingredient.cid]?.id === ingredient.id;

		if (exists) {
			delete modifiersNote.value[ingredient.cid];
		} else {
			modifiersNote.value[ingredient.cid] = { ...ingredient, type: 'ingredient' };
		}

		updateSelectedProduct();
	}

	function updateSelectedProduct() {
		// Собираем все id модификаторов
		const sortedModifiers = [...modifiersStorage.value].sort((a, b) => a.id - b.id);
		const modifiersIds = sortedModifiers.map(m => m.id);

		// Собираем все id ингредиентов
		const sortedIngredients = Object.values(modifiersNote.value).sort((a, b) => a.id - b.id);
		const ingredientsIds = sortedIngredients.map(i => i.id);

		// Строим итоговый product_order_index
		const allIds = [...modifiersIds, ...ingredientsIds].join('_');
		const productOrderIndex = `${product?.value?.id}_${allIds}`;

		// Обновляем выбранный продукт
		selectedProduct.value = {
			label: productName.value,
			id: product?.value?.id,
			price: productPrice.value,
			details: details.value,
			modifiers: sortedModifiers,
			notes: sortedIngredients,
			product_order_index: productOrderIndex
		};
	}

	function toSummary() {
		if (total.value === 0) {
			isDialogEmptyOrder.value = true;
			return;
		}

		router.push(makePath('/summary'));
	}
</script>

<style lang="scss" scoped>
	.option {
		max-width: calc(50% - 1.5rem / 2);
		min-width: 30%;
	}

	.product-image {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		overflow: hidden;

		img {
			object-fit: cover;
		}
	}
</style>